// Frontend logging utility to control console output
class FrontendLogger {
  private isDevelopment: boolean;
  private logLevel: 'error' | 'warn' | 'info' | 'debug';

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.logLevel = this.isDevelopment ? 'debug' : 'warn';
  }

  private shouldLog(level: 'error' | 'warn' | 'info' | 'debug'): boolean {
    if (!this.isDevelopment) {
      return level === 'error' || level === 'warn';
    }
    
    const levels = { error: 0, warn: 1, info: 2, debug: 3 };
    return levels[level] <= levels[this.logLevel];
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(`❌ [${this.getTimestamp()}] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(`⚠️ [${this.getTimestamp()}] ${message}`, ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog('info')) {
      console.log(`ℹ️ [${this.getTimestamp()}] ${message}`, ...args);
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(`🔍 [${this.getTimestamp()}] ${message}`, ...args);
    }
  }

  // Special method for subscription-related logs to reduce spam
  subscription(message: string, ...args: any[]): void {
    if (this.isDevelopment) {
      console.log(`🔍 [SUBSCRIPTION] ${message}`, ...args);
    }
  }

  // Special method for auth-related logs
  auth(message: string, ...args: any[]): void {
    if (this.isDevelopment) {
      console.log(`🔐 [AUTH] ${message}`, ...args);
    }
  }

  // Special method for data context logs
  dataContext(message: string, ...args: any[]): void {
    if (this.isDevelopment) {
      console.log(`📊 [DATA_CONTEXT] ${message}`, ...args);
    }
  }

  private getTimestamp(): string {
    return new Date().toLocaleTimeString();
  }
}

// Create singleton instance
const logger = new FrontendLogger();

export default logger;
